// Adapter + selector that powers the unified /posts grid.
//
// The grid mixes two data sources:
//   1. Posts (multi-platform projects) — lives in `useIdeaStore`. The PostIdea
//      already carries the rich Post-level data (idea, shape, candidate
//      platforms, confirmedPlatforms…) we need to render the IdeaCard.
//   2. Standalone single-platform Creatives (Assistant Chat / Weekly Cockpit)
//      — lives in `useCreativeStore`.
//
// The natural primary key for "is this card already covered?" is the
// creative's `projectId`:
//   • projectId matches a PostIdea  →  the PostIdea owns it; skip the creative.
//   • projectId is non-null but no PostIdea exists (legacy multi-platform
//     Project)  →  collapse the group into one synthetic card built from the
//     most-recently-updated member, scoped to its single platform. Other
//     siblings remain accessible via the existing composer flows.
//   • projectId is null  →  emit a synthetic single-platform IdeaCard.
//
// The synthetic projectId carries a `creative:<id>` sentinel so PostModal /
// PostPublishPanel can detect it and skip the project-confirm round trip.

import {
  PUBLISH_METADATA_KEY,
  livePlatformToSocial,
  type SocialPlatform,
} from '../utils/publish-creative'
import { CREATOR_MODE_MAP, CreativeStatus, CreativeType, type Creative } from '../types'
import type { ContentShape, LivePlatform, PostIdea } from '../store/idea-store'

const SYNTHETIC_PREFIX = 'creative:'

export const SYNTHETIC_PROJECT_PREFIX = SYNTHETIC_PREFIX

export function isSyntheticPostIdea(projectId: string): boolean {
  return projectId.startsWith(SYNTHETIC_PREFIX)
}

export function syntheticProjectIdToCreativeId(projectId: string): string | null {
  return projectId.startsWith(SYNTHETIC_PREFIX) ? projectId.slice(SYNTHETIC_PREFIX.length) : null
}

// Only social-platform creatives belong on /posts.
const SHAPE_BY_TYPE: Partial<Record<CreativeType, ContentShape>> = {
  [CreativeType.InstagramPost]: 'static_image',
  [CreativeType.FacebookPost]: 'static_image',
  [CreativeType.TikTokVideo]: 'short_vertical_video',
  [CreativeType.LinkedInPost]: 'static_image',
  [CreativeType.YouTubeShort]: 'short_vertical_video',
  [CreativeType.PinterestPin]: 'static_image',
  // X posts publish since 2026-09-06; a standalone (legacy, project-less)
  // X creative now gets the same scheduled / live / failed card as the rest.
  [CreativeType.XPost]: 'static_image',
}

function readPublishMeta(
  creative: Creative,
  channel: SocialPlatform
): { lastPublishedAt?: string; permalink?: string } | undefined {
  const metaKey = PUBLISH_METADATA_KEY[channel]
  const content = creative.content as unknown as Record<string, unknown> | null | undefined
  return content?.[metaKey] as { lastPublishedAt?: string; permalink?: string } | undefined
}

/**
 * Wrap a single standalone Creative as a PostIdea so IdeaCard can render it.
 * Returns null for creative types that have no PostModal-compatible publish
 * surface.
 */
export function creativeToPostIdea(creative: Creative): PostIdea | null {
  const shape = SHAPE_BY_TYPE[creative.type]
  if (!shape) return null

  const platform = (CREATOR_MODE_MAP[creative.type] ?? null) as LivePlatform | null
  if (!platform) return null

  const channel = livePlatformToSocial(platform)
  const meta = channel ? readPublishMeta(creative, channel) : undefined

  // Live = the server flipped status='published', publish metadata landed
  // (Pusher), OR the publishedAt column is set (the metadata block can be wiped
  // by a later edit while the column survives). Any signal is sufficient.
  const isLive =
    !!meta?.lastPublishedAt ||
    creative.status === CreativeStatus.Published ||
    !!creative.publishedAt
  // Scheduled = a future scheduledAt is set and we haven't published yet.
  const isScheduled =
    !isLive && !!creative.scheduledAt && new Date(creative.scheduledAt).getTime() > Date.now()

  const platformResults: PostIdea['platformResults'] | undefined = isLive
    ? {
        [platform]: {
          creativeId: creative.id,
          state: 'success',
          permalink: meta?.permalink,
        },
      }
    : undefined

  return {
    projectId: `${SYNTHETIC_PREFIX}${creative.id}`,
    idea: creative.name,
    shape,
    primaryPlatform: platform,
    candidatePlatforms: [platform],
    comingSoonPlatforms: [],
    status: isLive ? 'live' : isScheduled ? 'scheduled' : 'draft',
    primaryCreative: creative,
    isRegenerating: false,
    topicId: null,
    publishedAt: meta?.lastPublishedAt,
    scheduledAt: isScheduled ? (creative.scheduledAt ?? undefined) : undefined,
    platformResults,
  }
}

export type PostsFilter = 'draft' | 'scheduled' | 'live'

interface SelectArgs {
  ideas: PostIdea[]
  creatives: Creative[]
  filter: PostsFilter
}

/**
 * Build the unified, sorted, filtered list for /posts. Groups creatives by
 * `projectId` so siblings of one Post collapse into one card; standalone
 * creatives become their own card. PostIdeas in the idea-store always win
 * for their projectId — they carry the rich Post-level metadata we need.
 */
export function selectPostsPageItems({ ideas, creatives, filter }: SelectArgs): PostIdea[] {
  // PostIdea per projectId — the authoritative renderer for any Post project.
  const ideaByProjectId = new Map<string, PostIdea>()
  for (const idea of ideas) ideaByProjectId.set(idea.projectId, idea)

  // Bucket creatives: per-project groups for non-null projectId, orphans for
  // the rest. Filter out suggestions and types we can't render up front.
  const groupsByProjectId = new Map<string, Creative[]>()
  const orphans: Creative[] = []
  for (const c of creatives) {
    if (c.isSuggestion) continue
    if (!SHAPE_BY_TYPE[c.type]) continue
    if (c.projectId == null) {
      orphans.push(c)
      continue
    }
    const arr = groupsByProjectId.get(c.projectId) ?? []
    arr.push(c)
    groupsByProjectId.set(c.projectId, arr)
  }

  const items: PostIdea[] = []

  // 1. Every PostIdea — they own their projectId group regardless of which
  // siblings are loaded yet. Siblings of these projects are dropped silently
  // (they're already represented by the parent PostIdea's tabs in PostModal).
  for (const idea of ideas) {
    items.push(idea)
    groupsByProjectId.delete(idea.projectId)
  }

  // 2. Leftover non-null projectId groups — legacy multi-platform Projects
  // we don't have a PostIdea for. Collapse to a synthetic card built from
  // the most-recently-updated creative, scoped to that one platform. Avoids
  // fanning into PostModal's confirmIdea flow which can't confirm a non-post
  // Project.
  for (const group of groupsByProjectId.values()) {
    if (group.length === 0) continue
    const primary = group.reduce((latest, c) =>
      c.updatedAt.localeCompare(latest.updatedAt) > 0 ? c : latest
    )
    const synth = creativeToPostIdea(primary)
    if (synth) items.push(synth)
  }

  // 3. Orphan single-platform creatives (no projectId) — Assistant Chat output.
  for (const c of orphans) {
    const synth = creativeToPostIdea(c)
    if (synth) items.push(synth)
  }

  const filtered = items.filter(item => {
    if (filter === 'live') return item.status === 'live'
    if (filter === 'scheduled') return item.status === 'scheduled'
    // draft = anything not live and not scheduled (draft / publishing)
    return item.status !== 'live' && item.status !== 'scheduled'
  })

  return filtered.sort((a, b) => {
    const ta = a.primaryCreative?.updatedAt ?? a.publishedAt ?? ''
    const tb = b.primaryCreative?.updatedAt ?? b.publishedAt ?? ''
    return tb.localeCompare(ta)
  })
}
